<script>
import UploadV2 from "../pages/upload-v2/UploadV2.vue";
import { uploadFileMixins } from "../../mixins/fileUpload";
import { notificationMixins } from "../../mixins/notification";
import { uploadJobMixins } from "../../mixins/uploadJob";
import {
  clearActiveUpload,
  registerUploadManager,
  unregisterUploadManager,
  updateActiveUpload,
  uploadManagerState,
} from "../../util/uploadManager";
import {
  cleanupTerminalUploadJobs,
  deleteSuccessfulUploadEntries,
  deleteUploadJob,
  abandonLocalUploadJob,
  getAbandonedLocalUploadJobs,
  markLocalUploadJobCancelling,
} from "../../util/uploadIndexedDb";
import { CLIENT_UPLOAD_STATUS } from "../../util/uploadManifest";
import { uploadDiagnosticLog } from "../../util/uploadDiagnostics";

const UPLOAD_SETUP_PHASES = [
  "building-manifest",
  "creating-upload-job",
  "saving-upload-job",
  "starting-upload",
];
const LOCAL_UPLOAD_LEASE_TIMEOUT_MS = 120000;
const LOCAL_UPLOAD_ABANDON_SCAN_INTERVAL_MS = 10000;
const LOCAL_UPLOAD_ABANDON_INITIAL_DELAY_MS = 1000;
const MIN_TRANSFER_RATE_SAMPLE_INTERVAL_MS = 1000;

const runtimeMethods = { ...UploadV2.methods };
delete runtimeMethods.startUpload;
delete runtimeMethods.cancelUpload;

export default {
  name: "UploadManager",
  mixins: [uploadFileMixins, uploadJobMixins, notificationMixins],
  data() {
    return {
      activeJob: null,
      uploadJobId: null,
      uploadLeaseOwnerId: null,
      uploadLeaseTimer: null,
      uploadProgressWriteTimes: {},
      uploadProgressWriteSnapshots: {},
      pendingUploadProgress: {},
      uploadProgressFlushTimer: null,
      pendingUploadStatuses: {},
      uploadStatusFlushTimer: null,
      localJobSummary: null,
      uploadWorkerWatchdogTimer: null,
      uploadWorkerState: null,
      uploader: null,
      activeRunPromise: null,
      cancelRequested: false,
      suppressUploadErrors: false,
      currentItem: 0,
      currentFilename: "",
      currentPhase: "",
      currentPhaseProgress: { current: 0, total: 0 },
      totalFolders: 0,
      foldersDone: 0,
      progress: 0,
      uploadTransferRateBytesPerSecond: null,
      uploadRateLastBytes: null,
      uploadRateLastAt: null,
      uploadEntryRateSamples: {},
      uploadEntryTransferRates: {},
      abandoningLocalJobs: false,
      abandonTimer: null,
      abandonInitialTimer: null,
    };
  },
  methods: {
    ...runtimeMethods,
    isUploadSetupPhase() {
      return UPLOAD_SETUP_PHASES.includes(this.currentPhase);
    },
    applyPayload(payload) {
      this.folderId = payload.folderId;
      this.chosenFiles = payload.chosenFiles || [];
      this.isSecret = !!payload.isSecret;
      this.selectedRetentionSchedule = payload.selectedRetentionSchedule;
      this.currentSubjects = payload.currentSubjects || [];
      this.currentMetadata = payload.currentMetadata || [];
      this.folder = payload.folder || { id: payload.folderId };
      this.retryManifest = payload.retryManifest || null;
    },
    resetRuntimeState() {
      this.uploadJobId = null;
      this.uploadLeaseOwnerId = null;
      this.stopStatusFlushTimer?.();
      this.stopUploadWorkerWatchdog?.();
      this.uploadProgressWriteTimes = {};
      this.uploadProgressWriteSnapshots = {};
      this.pendingUploadProgress = {};
      this.pendingUploadStatuses = {};
      this.localJobSummary = null;
      this.uploadWorkerState = null;
      this.cancelRequested = false;
      this.suppressUploadErrors = false;
      this.currentItem = 0;
      this.currentFilename = "";
      this.currentPhase = "";
      this.currentPhaseProgress = { current: 0, total: 0 };
      this.totalFolders = 0;
      this.foldersDone = 0;
      this.progress = 0;
      this.resetUploadTransferStats();
      this.uploadManifest = null;
      this.retryManifest = null;
    },
    publishProgress(updates = {}) {
      if (!this.activeJob) {
        return;
      }
      const totalBytes = Number(this.localJobSummary?.totalBytes || 0);
      const uploadedBytes = Math.max(0, Math.min(
        totalBytes || Number.MAX_SAFE_INTEGER,
        Number(this.localJobSummary?.uploadedBytes || 0)
      ));
      const progressPercent = this.localJobProgressPercent
        ? this.localJobProgressPercent(this.localJobSummary)
        : (totalBytes > 0 ? Math.max(0, Math.min(100, Math.round((uploadedBytes / totalBytes) * 100))) : null);
      updateActiveUpload(this.activeJob.localUploadId, {
        uploadJobId: this.uploadJobId,
        currentItem: this.currentItem,
        currentFilename: this.currentFilename,
        currentPhase: this.currentPhase,
        currentPhaseProgress: this.currentPhaseProgress,
        totalFolders: this.totalFolders,
        foldersDone: this.foldersDone,
        totalFiles: this.chosenFiles?.length || 0,
        progress: this.progress,
        totalBytes,
        uploadedBytes,
        progressPercent,
        totalEntryCount: this.localJobSummary?.totalEntryCount || 0,
        completedEntryCount: this.localJobSummary?.completedEntryCount || 0,
        failedEntryCount: this.localJobSummary?.failedEntryCount || 0,
        totalFileCount: this.localJobSummary?.totalFileCount || 0,
        completedFileCount: this.localJobSummary?.completedFileCount || 0,
        failedFileCount: this.localJobSummary?.failedFileCount || 0,
        totalFolderCount: this.localJobSummary?.totalFolderCount || 0,
        completedFolderCount: this.localJobSummary?.completedFolderCount || 0,
        failedFolderCount: this.localJobSummary?.failedFolderCount || 0,
        uploadTransferRateBytesPerSecond: this.uploadTransferRateBytesPerSecond,
        uploadEntryTransferRates: this.uploadEntryTransferRates,
        ...updates,
      });
    },
    resetUploadTransferStats() {
      this.uploadTransferRateBytesPerSecond = null;
      this.uploadRateLastBytes = null;
      this.uploadRateLastAt = null;
      this.uploadEntryRateSamples = {};
      this.uploadEntryTransferRates = {};
    },
    hasEntryTransferRateSamples() {
      return Object.keys(this.uploadEntryRateSamples || {}).length > 0;
    },
    updateAggregateTransferRateFromEntryRates() {
      const rates = Object.values(this.uploadEntryTransferRates || {})
        .map((rate) => Number(rate || 0))
        .filter((rate) => rate > 0);
      this.uploadTransferRateBytesPerSecond = rates.length
        ? rates.reduce((total, rate) => total + rate, 0)
        : null;
    },
    clearEntryUploadTransferRate(clientEntryId) {
      if (!clientEntryId) {
        return;
      }
      const nextSamples = { ...(this.uploadEntryRateSamples || {}) };
      const nextRates = { ...(this.uploadEntryTransferRates || {}) };
      delete nextSamples[clientEntryId];
      delete nextRates[clientEntryId];
      this.uploadEntryRateSamples = nextSamples;
      this.uploadEntryTransferRates = nextRates;
      this.updateAggregateTransferRateFromEntryRates();
    },
    recordEntryUploadTransferProgress(clientEntryId, uploadedBytes, sampledAt = Date.now()) {
      if (!clientEntryId) {
        return;
      }
      const uploaded = Number(uploadedBytes || 0);
      const sampledAtMs = Number(sampledAt);
      if (!Number.isFinite(sampledAtMs)) {
        return;
      }
      const previous = this.uploadEntryRateSamples[clientEntryId];
      if (!previous || uploaded < previous.uploadedBytes) {
        this.uploadEntryRateSamples = {
          ...this.uploadEntryRateSamples,
          [clientEntryId]: { uploadedBytes: uploaded, sampledAt: sampledAtMs },
        };
        if (this.uploadEntryTransferRates[clientEntryId] !== undefined) {
          const remainingRates = { ...this.uploadEntryTransferRates };
          delete remainingRates[clientEntryId];
          this.uploadEntryTransferRates = remainingRates;
          this.updateAggregateTransferRateFromEntryRates();
        }
        return;
      }
      const elapsedMs = sampledAtMs - previous.sampledAt;
      if (elapsedMs < MIN_TRANSFER_RATE_SAMPLE_INTERVAL_MS) {
        return;
      }
      const byteDelta = uploaded - previous.uploadedBytes;
      this.uploadEntryRateSamples = {
        ...this.uploadEntryRateSamples,
        [clientEntryId]: { uploadedBytes: uploaded, sampledAt: sampledAtMs },
      };
      if (byteDelta <= 0) {
        return;
      }
      const currentRate = byteDelta / (elapsedMs / 1000);
      const previousRate = this.uploadEntryTransferRates[clientEntryId];
      this.uploadEntryTransferRates = {
        ...this.uploadEntryTransferRates,
        [clientEntryId]: previousRate === undefined
          ? currentRate
          : (previousRate * 0.7) + (currentRate * 0.3),
      };
      this.updateAggregateTransferRateFromEntryRates();
    },
    recordUploadTransferProgress(uploadedBytes) {
      const uploaded = Number(uploadedBytes || 0);
      const now = Date.now();
      if (this.uploadRateLastAt === null || this.uploadRateLastBytes === null || uploaded < this.uploadRateLastBytes) {
        this.uploadRateLastBytes = uploaded;
        this.uploadRateLastAt = now;
        return;
      }
      const elapsedMs = now - this.uploadRateLastAt;
      if (elapsedMs < MIN_TRANSFER_RATE_SAMPLE_INTERVAL_MS) {
        return;
      }
      const byteDelta = uploaded - this.uploadRateLastBytes;
      if (byteDelta > 0) {
        const currentRate = byteDelta / (elapsedMs / 1000);
        this.uploadTransferRateBytesPerSecond = this.uploadTransferRateBytesPerSecond === null
          ? currentRate
          : (this.uploadTransferRateBytesPerSecond * 0.7) + (currentRate * 0.3);
      }
      this.uploadRateLastBytes = uploaded;
      this.uploadRateLastAt = now;
    },
    updateOverallUploadByteProgress(uploadedBytes, totalBytes, { recordRate = true } = {}) {
      const total = Number(totalBytes || 0);
      if (total <= 0) {
        return;
      }
      const uploaded = Math.max(0, Math.min(total, Number(uploadedBytes || 0)));
      this.progress = uploaded / total;
      if (recordRate && !this.hasEntryTransferRateSamples()) {
        this.recordUploadTransferProgress(uploaded);
      }
      this.publishProgress({ status: "uploading" });
    },
    applyByteAggregateProgress() {
      if (!this.localJobSummary || Number(this.localJobSummary.totalBytes || 0) <= 0) {
        return false;
      }
      this.updateOverallUploadByteProgress(this.localJobSummary.uploadedBytes, this.localJobSummary.totalBytes);
      return true;
    },
    flushTerminalUploadTelemetry(terminalStatus, extra = {}) {
      if (!this.uploadJobId) {
        return;
      }
      this.flushUploadJobClientTelemetry(this.uploadJobId, terminalStatus, {
        targetFolderId: this.folderId,
        leaseOwnerId: this.uploadLeaseOwnerId,
        manifest: this.uploadManifest,
        entries: this.uploadManifest?.entries,
        timings: {
          currentPhase: this.currentPhase,
          currentPhaseProgress: this.currentPhaseProgress,
          uploadStages: this.uploadTimingTelemetry?.() || {},
        },
        localIndexedDb: this.localJobSummary ? {
          totalEntryCount: this.localJobSummary.totalEntryCount,
          completedEntryCount: this.localJobSummary.completedEntryCount,
          failedEntryCount: this.localJobSummary.failedEntryCount,
          completedFileCount: this.localJobSummary.completedFileCount,
          failedFileCount: this.localJobSummary.failedFileCount,
          completedFolderCount: this.localJobSummary.completedFolderCount,
          failedFolderCount: this.localJobSummary.failedFolderCount,
          uploadedBytes: this.localJobSummary.uploadedBytes,
        } : {},
        ...extra,
      }).catch(console.error);
    },
    async cleanupTerminalLocalUpload(runResult, uploadJobId = this.uploadJobId) {
      if (!uploadJobId) {
        return;
      }
      if (runResult?.result?.cleanupLocalJob) {
        await deleteUploadJob(uploadJobId).catch(console.error);
      } else if (runResult?.result?.cleanupSuccessfulEntries) {
        await deleteSuccessfulUploadEntries(uploadJobId).catch(console.error);
      }
    },
    startQueuedUpload(job) {
      if (this.activeJob) {
        throw new Error("An upload is already in progress");
      }
      this.activeJob = job;
      this.resetRuntimeState();
      this.applyPayload(job.payload);
      const runPromise = this.runActiveUpload().catch((error) => {
        console.error(error);
      }).finally(() => {
        if (this.activeRunPromise === runPromise) {
          this.activeRunPromise = null;
        }
      });
      this.activeRunPromise = runPromise;
    },
    async runActiveUpload() {
      const localUploadId = this.activeJob.localUploadId;
      cleanupTerminalUploadJobs().catch(console.error);
      this.resetUploadTimingSummary?.();
      this.publishProgress({ status: "uploading" });
      this.uploader = this.uploadFilesV2(
        (item, fileName, numberOfItems, phase, phaseProgress) => {
          this.currentItem = Math.floor(item);
          this.currentFilename = fileName;
          this.currentPhase = phase || "uploading-files";
          this.currentPhaseProgress = phaseProgress || { current: 0, total: 0 };
          if (phaseProgress && this.currentPhase === "creating-folders") {
            this.totalFolders = phaseProgress.total || 0;
            this.foldersDone = phaseProgress.current || 0;
          }
          if (this.isUploadSetupPhase()) {
            this.progress = 0;
            this.publishProgress({ status: "uploading" });
            return;
          }
          const totalItems = numberOfItems || 0;
          const totalWork = this.totalFolders + totalItems;
          const totalBytes = Number(this.localJobSummary?.totalBytes || 0);
          if (totalWork === 0) {
            this.progress = 0;
          } else if (this.currentPhase === "creating-folders") {
            this.progress = totalBytes > 0 ? 0 : this.foldersDone / totalWork;
          } else {
            const filesDone = Math.min(item, totalItems);
            if (totalBytes > 0) {
              this.applyByteAggregateProgress();
            } else {
              this.progress = (this.foldersDone + filesDone) / totalWork;
            }
          }
          this.publishProgress({ status: "uploading" });
        },
        (item, error) => {
          if (this.suppressUploadErrors) {
            return;
          }
          const message = error?.message || error?.userMessage || this.$t("upload-error", { filename: item.file.name });
          this.showError(message, { expiryTime: 30000 });
        }
      );

      let runResult = null;
      let success = false;
      try {
        runResult = await this.uploader.run();
        const cancelled = !!runResult?.cancelled;
        const externallyCancelled = !!runResult?.result?.cancelled;
        const anySuccess = runResult?.result?.anySuccess;
        success = !cancelled && !externallyCancelled && anySuccess !== false;
      } catch (error) {
        uploadDiagnosticLog("error", "upload-execution-failed", {
          uploadJobId: this.uploadJobId,
        }, error);
        console.error(error);
        if (this.uploadJobId) {
          await this.persistLocalJobSummary(this.uploadJobId, { clientJobStatus: CLIENT_UPLOAD_STATUS.ERROR }).catch(console.error);
        }
      } finally {
        this.stopUploadJobKeepAlive();
        await this.clearLocalLease();
      }

      this.uploader = null;
      if (runResult?.cancelled || runResult?.result?.cancelled || this.cancelRequested) {
        this.flushTerminalUploadTelemetry(CLIENT_UPLOAD_STATUS.CANCELLED);
        this.publishProgress({ status: "cancelled" });
        window.setTimeout(() => clearActiveUpload(localUploadId), 5000);
        this.activeJob = null;
        return;
      }
      if (success) {
        const completedUploadJobId = this.uploadJobId;
        const completedFolderId = this.folder?.id;
        const hasEntryErrors = this.uploadManifest?.entries?.some((entry) => entry.clientStatus === CLIENT_UPLOAD_STATUS.ERROR);
        const terminalStatus = hasEntryErrors
          ? CLIENT_UPLOAD_STATUS.COMPLETE_WITH_ERRORS
          : CLIENT_UPLOAD_STATUS.COMPLETE;
        this.flushTerminalUploadTelemetry(terminalStatus);
        const sensitiveDetections = runResult?.result?.sensitiveDetections || [];
        this.publishProgress({ status: "complete", terminalStatus, hasEntryErrors, progress: 1, sensitiveDetections });
        this.activeJob = null;
        await this.cleanupTerminalLocalUpload(runResult, completedUploadJobId);
        if (completedFolderId) {
          await this.touchFolder(completedFolderId).catch(console.error);
        }
      } else {
        this.flushTerminalUploadTelemetry("FAILED");
        this.publishProgress({ status: "failed", errorMessage: this.$t("upload-error", { filename: "" }) });
        this.activeJob = null;
      }
      window.setTimeout(() => clearActiveUpload(localUploadId), 5000);
    },
    async cancelActiveUpload() {
      if (!this.activeJob) {
        return;
      }
      this.cancelRequested = true;
      this.suppressUploadErrors = true;
      this.publishProgress({ status: "cancelling" });
      if (this.uploadJobId) {
        await markLocalUploadJobCancelling(this.uploadJobId).catch(console.error);
      }
      if (this.uploader) {
        await this.uploader.cancel();
      }
      if (this.activeRunPromise) {
        await this.activeRunPromise;
      }
    },
    async abandonStaleLocalUploads() {
      if (this.abandoningLocalJobs) {
        return;
      }
      this.abandoningLocalJobs = true;
      try {
        const abandonedJobs = await getAbandonedLocalUploadJobs(LOCAL_UPLOAD_LEASE_TIMEOUT_MS);
        for (const job of abandonedJobs) {
          if (this.isUploadJobActiveInThisSession(job.jobId)) {
            uploadDiagnosticLog("info", "my-uploads-stale-lease-active-upload-skip", {
              uploadJobId: job.jobId,
              leaseOwnerId: job.leaseOwnerId,
              leaseLastSeenAt: job.leaseLastSeenAt,
            });
            continue;
          }
          uploadDiagnosticLog("warn", "my-uploads-abandon-stale-lease", {
            uploadJobId: job.jobId,
            leaseOwnerId: job.leaseOwnerId,
            leaseLastSeenAt: job.leaseLastSeenAt,
          });
          await abandonLocalUploadJob(job.jobId);
          await this.flushUploadJobClientTelemetry(job.jobId, CLIENT_UPLOAD_STATUS.ABANDONED, {
            targetFolderId: job.folderId,
            leaseOwnerId: job.leaseOwnerId,
            terminalReason: "stale-local-lease",
            localIndexedDb: {
              totalEntryCount: job.totalEntryCount,
              completedEntryCount: job.completedEntryCount,
              failedEntryCount: job.failedEntryCount,
              totalFileCount: job.totalFileCount,
              completedFileCount: job.completedFileCount,
              failedFileCount: job.failedFileCount,
              totalFolderCount: job.totalFolderCount,
              completedFolderCount: job.completedFolderCount,
              failedFolderCount: job.failedFolderCount,
              uploadedBytes: job.uploadedBytes,
              leaseLastSeenAt: job.leaseLastSeenAt,
            },
          }).catch(console.error);
          await this.abandonUploadJob(job.jobId).catch((error) => {
            uploadDiagnosticLog("warn", "my-uploads-backend-abandon-failed", {
              uploadJobId: job.jobId,
            }, error);
            console.error(error);
          });
        }
      } finally {
        this.abandoningLocalJobs = false;
      }
    },
    isUploadJobActiveInThisSession(jobId) {
      if (!jobId) {
        return false;
      }
      const activeUpload = uploadManagerState.activeUpload;
      const activeStatus = activeUpload?.status;
      if (
        (activeUpload?.uploadJobId || activeUpload?.retryOfJobId) &&
        [activeUpload.uploadJobId, activeUpload.retryOfJobId].some((activeJobId) => String(activeJobId) === String(jobId)) &&
        activeStatus !== "complete" &&
        activeStatus !== "failed" &&
        activeStatus !== "cancelled"
      ) {
        return true;
      }
      return !!this.activeJob && String(this.uploadJobId) === String(jobId);
    },
    startStaleLeaseMaintenance() {
      this.abandonInitialTimer = window.setTimeout(() => {
        this.abandonInitialTimer = null;
        this.abandonStaleLocalUploads().catch(console.error);
      }, LOCAL_UPLOAD_ABANDON_INITIAL_DELAY_MS);
      this.abandonTimer = window.setInterval(() => {
        this.abandonStaleLocalUploads().catch(console.error);
      }, LOCAL_UPLOAD_ABANDON_SCAN_INTERVAL_MS);
    },
    stopStaleLeaseMaintenance() {
      if (this.abandonInitialTimer) {
        window.clearTimeout(this.abandonInitialTimer);
        this.abandonInitialTimer = null;
      }
      if (this.abandonTimer) {
        window.clearInterval(this.abandonTimer);
        this.abandonTimer = null;
      }
    },
    beforeWindowUnload(event) {
      if (!this.uploader) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    },
  },
  created() {
    registerUploadManager(this);
    this.startStaleLeaseMaintenance();
    window.addEventListener("beforeunload", this.beforeWindowUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeWindowUnload);
    this.stopStaleLeaseMaintenance();
    unregisterUploadManager(this);
    if (this.uploader) {
      uploadDiagnosticLog("info", "upload-manager-shutdown-active-upload-detached", {
        uploadJobId: this.uploadJobId,
      });
      this.stopUploadJobKeepAlive?.();
    }
    this.stopLocalLease();
  },
  render(h) {
    return h("span", { style: { display: "none" } });
  },
};
</script>
